import { useNavigate } from 'react-router-dom';
import { Banknote, ArrowRight } from 'lucide-react';
import { useDebts } from '../hooks/useDebts.js';
import { formatCurrency } from '../lib/format.js';
import { SOURCE_TYPE_LABELS } from '../lib/debtHelpers.js';

export default function DebtsSummaryWidget() {
  const navigate = useNavigate();
  const { data, isLoading } = useDebts();

  const debts = Array.isArray(data) ? data : data?.debts ?? [];
  const active = debts.filter((d) => d.status === 'active');

  if (isLoading) return null;
  if (!active.length) return null;

  const totalRemaining = active.reduce((sum, d) => sum + (d.remaining_cents ?? 0), 0);
  const totalMonthly = active.reduce((sum, d) => sum + (d.monthly_payment_cents ?? 0), 0);

  const top3 = [...active]
    .sort((a, b) => (b.remaining_cents ?? 0) - (a.remaining_cents ?? 0))
    .slice(0, 3);

  return (
    <div className="rounded-xl bg-white border border-slate-200 shadow-sm px-4 py-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Banknote size={16} className="text-slate-500" aria-hidden />
          <h2 className="text-sm font-semibold text-slate-800">Dettes externes</h2>
        </div>
        <button
          type="button"
          onClick={() => navigate('/dettes')}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 transition-colors"
          aria-label="Voir toutes les dettes"
        >
          Voir toutes les dettes
          <ArrowRight size={12} />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs text-slate-500 mb-0.5">Capital restant dû</p>
          <p className="text-xl font-bold tabular-nums text-slate-900">{formatCurrency(totalRemaining)}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-0.5">Provision mensuelle</p>
          <p className="text-xl font-bold tabular-nums text-slate-900">{formatCurrency(totalMonthly)}</p>
        </div>
      </div>

      {top3.length > 0 && (
        <ul className="space-y-1.5">
          {top3.map((d) => (
            <li key={d.id} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <span className="truncate text-slate-700 font-medium">{d.name}</span>
                <span className="text-xs text-slate-400 shrink-0">
                  {SOURCE_TYPE_LABELS[d.source_type] ?? d.source_type}
                </span>
              </div>
              <span className="tabular-nums text-slate-600 shrink-0 ml-2">
                {formatCurrency(d.remaining_cents)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
